import React, { useState, useEffect } from "react";
import { CSVLink } from 'react-csv';
import { serviceWhatsappData, serviceSmsData, serviceCallData } from "../../../Services/Service";

const CommunicationModal = ({
    show,
    onClose,
    title,
    loading,
    list,
    customHeaders,
    filterForm,
    currentPage = 1,
    totalPages = 1,
    totalRecords = 0,
    rowsPerPage = 10,
    onPageChange,
    onRowsPerPageChange,
    modalDataApi = "",
    lastMonth = "",
    today = "",
    currentListPayload = null
}) => {
    if (!show) return null;

    // Local state for export
    const [csvData, setCsvData] = useState([]);
    const [loadingExport, setLoadingExport] = useState(false);
    const [showExportButton, setShowExportButton] = useState(true);
    const [exportPayload, setExportPayload] = useState(null);

    // Store the current filter payload for export
    useEffect(() => {
        if (show) {
            if (currentListPayload) {
                console.log("Using provided list payload for export:", currentListPayload);
                setExportPayload(currentListPayload);
            }
            else if (filterForm) {
                const payload = {
                    startRange: filterForm.startRange,
                    endRange: filterForm.endRange,
                    communicationType: title,
                    divisionClinicFilter: filterForm.divisionClinicFilter,
                    programSelect: filterForm.programSelect,
                    page: '',
                    limit: '',
                    exportType: 'excel'
                };
                console.log("Reconstructed export payload:", payload);
                setExportPayload(payload);
            }
        }
    }, [show, filterForm, title, currentListPayload]);

    // Convert list safely into array - handle both WhatsApp and SMS response structures
    const arrayList = React.useMemo(() => {
        if (!list) return [];

        // If list has a message property but no data
        if (list.message && !list.data) {
            console.log("API message received:", list.message);
            return [];
        }

        // Check if this is an SMS response (has data.communicationlist)
        if (list.data && list.data.communicationlist && Array.isArray(list.data.communicationlist)) {
            console.log("Detected SMS response structure, using communicationlist");
            return list.data.communicationlist;
        }
        
        // Check if this is a WhatsApp response (has data array directly)
        if (list.data && Array.isArray(list.data)) {
            console.log("Detected WhatsApp response structure, using data array directly");
            return list.data;
        }

        // If list itself is an array
        if (Array.isArray(list)) {
            return list;
        }

        return [];
    }, [list]);

    // Table Headers
    const tableHeaders = customHeaders?.length
        ? customHeaders
        : ["Year", "Month/Week/Date", "Conversation Count"];

    // Build CSV data
    const buildCsvForExport = (data) => {
        return data.map((row, idx) => {
            const out = {};
            tableHeaders.forEach((h) => {
                const key = h.replace(/\s+/g, '_');
                const lower = h.toLowerCase();
                switch (lower) {
                    case 'sr no':
                        out[key] = idx + 1;
                        break;
                    case 'year':
                        out[key] = row.year || row.yearCount || '-';
                        break;
                    case 'month/week/date':
                        out[key] = row.month || row.week || row.date || row.monthCount || '-';
                        break;
                    case 'conversation count':
                        out[key] = row.count || row.total_conversations || row.conversationCount || '-';
                        break;
                    case 'mobile no':
                        out[key] = (row.mobile || '-').replace(/^\+/, '');
                        break;
                    case 'text':
                        out[key] = row.smsText || row.whatsappText || '-';
                        break;
                    case 'status':
                        out[key] = row.status || '-';
                        break;
                    case 'sent date':
                        out[key] = row.createdTime || '-';
                        break;
                    default:
                        const compact = lower.replace(/\s+/g, '');
                        out[key] = row[compact] || row[h] || row[key] || '';
                }
            });
            return out;
        });
    };

    const fetchExportData = async () => {
        if (!exportPayload) {
            alert("Cannot export: Filter data not available");
            return;
        }

        setLoadingExport(true);
        setCsvData([]);

        try {
            console.log("Exporting with payload:", exportPayload);

            let allData = [];
            let response;

            if (modalDataApi === "whatsappsData") {
                response = await serviceWhatsappData(
                    exportPayload.startRange,
                    exportPayload.endRange,
                    exportPayload.communicationType,
                    exportPayload.divisionClinicFilter,
                    exportPayload.programSelect,
                    '', '', 'excel'
                );
                // WhatsApp returns data directly in response.data.data
                allData = response?.data?.data || [];

            } else if (modalDataApi === "smssData") {
                response = await serviceSmsData(
                    exportPayload.startRange,
                    exportPayload.endRange,
                    exportPayload.communicationType,
                    exportPayload.divisionClinicFilter,
                    exportPayload.programSelect,
                    '', '', 'excel'
                );
                // SMS returns data in response.data.data.communicationlist
                allData = response?.data?.data?.communicationlist || [];

            } else if (modalDataApi === "callsData") {
                response = await serviceCallData(
                    exportPayload.startRange,
                    exportPayload.endRange,
                    exportPayload.communicationType,
                    exportPayload.divisionClinicFilter,
                    exportPayload.programSelect,
                    '', '', 'excel'
                );
                // Assuming calls returns data directly like WhatsApp
                allData = response?.data?.data || [];
            }

            console.log("Export response:", response?.data);
            console.log("Export data length:", allData.length);

            if (allData.length === 0) {
                alert("No data available for export with current filters");
                setLoadingExport(false);
                return;
            }

            const csv = buildCsvForExport(allData);
            setCsvData(csv);
            setShowExportButton(false);

        } catch (err) {
            console.error('Error fetching export data', err);
            
            console.log("Current exportPayload:", exportPayload);
            console.log("Modal Data API:", modalDataApi);
            
            // Fallback to current page data
            if (arrayList.length > 0) {
                const csv = buildCsvForExport(arrayList);
                setCsvData(csv);
                setShowExportButton(false);
                alert(`Full export failed. Downloaded current page (${arrayList.length} records) instead.`);
            } else {
                alert(`Export failed: ${err.message}`);
            }
        } finally {
            setLoadingExport(false);
        }
    };

    // Calculate display range
    const startItem = (currentPage - 1) * rowsPerPage + 1;
    const endItem = Math.min(currentPage * rowsPerPage, totalRecords);

    return (
        <div
            className="modal fade show d-block"
            tabIndex="-1"
            style={{ background: "rgba(0,0,0,0.5)" }}
        >
            <div className="modal-dialog modal-xl">
                <div className="modal-content">
                    <div className="modal-header">
                        <h5 className="modal-title">{title}</h5>
                        <button type="button" className="btn-close" onClick={onClose} />
                    </div>

                    <div className="modal-body">
                        {loading ? (
                            <div className="text-center p-4">Loading...</div>
                        ) : arrayList.length === 0 ? (
                            <div className="text-center p-4">
                                <p>No data found.</p>
                                {list?.message && (
                                    <small className="text-muted">
                                        {list.message}
                                    </small>
                                )}
                            </div>
                        ) : (
                            <>
                                <div className="table-responsive">
                                    <table className="table table-bordered table-striped">
                                        <thead>
                                            <tr>
                                                {tableHeaders.map((header, index) => (
                                                    <th key={index}>{header}</th>
                                                ))}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {arrayList.map((row, i) => (
                                                <tr key={`row-${currentPage}-${i}-${row.id || i}`}>
                                                    {tableHeaders.map((header, idx) => {
                                                        let fieldValue = '-';
                                                        const headerLower = header.toLowerCase();

                                                        switch (headerLower) {
                                                            case 'sr no':
                                                                fieldValue = (currentPage - 1) * rowsPerPage + i + 1;
                                                                break;
                                                            case 'year':
                                                                fieldValue = row.year || row.yearCount || '-';
                                                                break;
                                                            case 'month/week/date':
                                                                fieldValue = row.month || row.week || row.date || row.monthCount || '-';
                                                                break;
                                                            case 'conversation count':
                                                                fieldValue = row.count || row.total_conversations || row.conversationCount || '-';
                                                                break;
                                                            case 'mobile no':
                                                                fieldValue = (row.mobile || '-').replace(/^\+/, '');
                                                                break;
                                                            case 'text':
                                                                fieldValue = row.smsText || row.whatsappText || '-';
                                                                break;
                                                            case 'status':
                                                                fieldValue = row.status || '-';
                                                                break;
                                                            case 'sent date':
                                                                fieldValue = row.createdTime || '-';
                                                                break;
                                                            default:
                                                                const headerKey = headerLower.replace(/\s+/g, '');
                                                                fieldValue = row[headerKey] || row[header] || '-';
                                                        }

                                                        if (headerLower === 'text') {
                                                            return (
                                                                <td key={`cell-${idx}-${i}-${row.id || i}`}>
                                                                    <div style={{
                                                                        whiteSpace: 'normal',
                                                                        wordBreak: 'break-word'
                                                                    }}>
                                                                        {fieldValue}
                                                                    </div>
                                                                </td>
                                                            );
                                                        } else {
                                                            return <td key={`cell-${idx}-${i}-${row.id || i}`}>{fieldValue}</td>;
                                                        }
                                                    })}
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>

                                {/* Pagination Controls */}
                                {totalPages > 1 && (
                                    <div className="mt-4">
                                        <div className="d-flex justify-content-between align-items-center">
                                            <div className="d-flex align-items-center gap-3">
                                                <label htmlFor="rowsPerPageSelect" className="form-label mb-0">
                                                    Rows per page:
                                                </label>
                                                <select
                                                    id="rowsPerPageSelect"
                                                    className="form-select"
                                                    style={{ width: '70px' }}
                                                    value={rowsPerPage}
                                                    onChange={(e) => {
                                                        const newRowsPerPage = parseInt(e.target.value, 10);
                                                        if (onRowsPerPageChange) {
                                                            onRowsPerPageChange(newRowsPerPage);
                                                        }
                                                    }}
                                                    disabled={loading}
                                                >
                                                    <option value={10}>10</option>
                                                    <option value={25}>25</option>
                                                    <option value={50}>50</option>
                                                    <option value={100}>100</option>
                                                </select>
                                            </div>

                                            <div className="text-muted">
                                                {startItem}-{endItem} of {totalRecords}
                                            </div>

                                            <div className="d-flex align-items-center gap-2">
                                                <button
                                                    className="btn btn-sm btn-outline-secondary"
                                                    disabled={currentPage <= 1 || loading}
                                                    onClick={() => onPageChange && onPageChange(1)}
                                                    title="First page"
                                                >
                                                    ⏮
                                                </button>

                                                <button
                                                    className="btn btn-sm btn-outline-secondary"
                                                    disabled={currentPage === 1 || loading}
                                                    onClick={() => onPageChange && onPageChange(currentPage - 1)}
                                                    title="Previous"
                                                >
                                                    ❮
                                                </button>

                                                <span className="px-2" style={{ minWidth: '120px', textAlign: 'center' }}>
                                                    Page {currentPage} of {totalPages}
                                                </span>

                                                <button
                                                    className="btn btn-sm btn-outline-secondary"
                                                    disabled={currentPage === totalPages || loading}
                                                    onClick={() => onPageChange && onPageChange(currentPage + 1)}
                                                    title="Next"
                                                >
                                                    ❯
                                                </button>

                                                <button
                                                    className="btn btn-sm btn-outline-secondary"
                                                    disabled={currentPage >= totalPages || loading}
                                                    onClick={() => onPageChange && onPageChange(totalPages)}
                                                    title="Last page"
                                                >
                                                    ⏭
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                <div className="mt-3 text-end">
                                    {showExportButton && (
                                        <button
                                            className="btn btn-success me-2"
                                            onClick={fetchExportData}
                                            disabled={loadingExport || !modalDataApi || !exportPayload}
                                            title={exportPayload ? 
                                                `Export with current filters` : 
                                                "No filter data available"}
                                        >
                                            {loadingExport ? 'Preparing Data...' : 'Prepare Data to Download'}
                                        </button>
                                    )}

                                    {csvData && csvData.length > 0 && (
                                        <CSVLink
                                            headers={tableHeaders.map(h => ({ label: h, key: h.replace(/\s+/g, '_') }))}
                                            data={csvData}
                                            filename={`${title ? title.replace(/\s+/g, '_').toLowerCase() : 'export'}_${new Date().toISOString().split('T')[0]}.csv`}
                                            className="btn btn-success"
                                            onClick={() => {
                                                setTimeout(() => {
                                                    setCsvData([]);
                                                    setShowExportButton(true);
                                                }, 1000);
                                            }}
                                        >
                                            Download CSV ({csvData.length} records)
                                        </CSVLink>
                                    )}
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CommunicationModal;